/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Checkbox, InlineNotification, LoadingSection, Typography2 } from '@coursera/cds-core';
import type { PreviewImportResponse } from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/bulkprograminvitations/v1/bulk_program_invitations_api';

import { CSV_TEMPLATE_URL } from 'bundles/enterprise-admin-constants/ProgramMembersConstants';
import ProgramInvitationsApiManager from 'bundles/enterprise-admin-program-users/utils/ProgramInvitationsApiManager';
import ModalFooter from 'bundles/enterprise-admin-users/components/ModalFooter';
import CSVPreUploadPreview from 'bundles/enterprise-admin-users/components/csvImportWidget/CSVPreUploadPreviewV2';
import FileUploadWidget from 'bundles/enterprise-admin-users/components/csvImportWidget/FileUploadWidgetV2';
import CSVInvitationModel from 'bundles/enterprise-admin-users/models/CSVInvitation';
import type { ImportProcessingTypes, Program, S3Location, UserRoster } from 'bundles/enterprise-admin-users/types';

import _t from 'i18n!nls/enterprise-admin-users';

type Error = {
  message: string | JSX.Element;
  type: 'warning' | 'error';
};

const getDefaultError = (): Error => ({
  message: _t('Error uploading. Please try again later.'),
  type: 'error',
});

type Props = {
  selectedRoster?: UserRoster;
  program?: Program;
  file?: File;
  setFile: (file?: File) => void;
  setUploadResults: (
    s3Location: S3Location,
    importProcessingType: ImportProcessingTypes,
    csvInvitation: CSVInvitationModel
  ) => void;
  handleClose: () => void;
  onPreviousStep?: () => void;
  onContinue: () => void;
  didAgreeToTerms?: boolean;
  onAgreementToTermsChange?: () => void;
  courseId?: string;
};

type State = {
  error?: Error;
  loading: boolean;
  uploadAttemptWithoutFile?: boolean;
  isInitialState?: boolean;
};

type Context = {
  executeMutation: $TSFixMe;
};

const confirm = css({
  fontSize: '14px',
  color: '#D20001',
  marginBottom: '10px',
  marginLeft: '43px',
});

class ImportLearnersCSVUpload extends React.Component<Props, State> {
  declare _apiManager: ProgramInvitationsApiManager;

  static contextTypes = {
    executeMutation: PropTypes.func,
  };

  declare context: LegacyContextType<typeof ImportLearnersCSVUpload.contextTypes>;

  constructor(props: Props, context: Context) {
    super(props, context);
    const { executeMutation } = this.context;
    const { program } = this.props;
    this._apiManager = new ProgramInvitationsApiManager({ naptime: { executeMutation }, program });
    this.state = {
      loading: false,
      isInitialState: true,
    };
  }

  handleS3Upload = () => {
    const { file } = this.props;
    if (!file) {
      this.setState({ uploadAttemptWithoutFile: true });
      return;
    }
    this.setState({ uploadAttemptWithoutFile: false });
    const apiPromise = this._apiManager.uploadFile(file);
    this.setState({ loading: true });
    apiPromise
      .then((response: { s3Location: { key: string; bucket: string } }) => {
        const { s3Location } = response;
        this.handleCSVPreview(s3Location);
      })
      .catch(() => {
        this.setState({ error: getDefaultError(), loading: false });
      });
  };

  handleCSVPreview = (s3Location: { key: string; bucket: string }) => {
    const { selectedRoster } = this.props;
    const { key, bucket } = s3Location;
    const rosterId = selectedRoster?.id;
    const apiPromise = this._apiManager.getPreviewImportPromise({
      rosterId,
      s3Key: key,
      s3Bucket: bucket,
    });
    apiPromise.then((response) => {
      if (response.ok) {
        response.json().then((data: PreviewImportResponse) => {
          const apiData = data.async || data.sync;
          const typeName = data.async ? 'asynchronousImport' : 'synchronousImport';
          const csvInvitation = new CSVInvitationModel({ apiData });
          const { totalSkippedCount, invitationCount } = csvInvitation;
          if (invitationCount === 0) {
            const error = this.getInvitationError(totalSkippedCount);
            this.setState({ error, loading: false });
          } else {
            const { setUploadResults, onContinue } = this.props;
            setUploadResults(s3Location, typeName, csvInvitation);
            onContinue();
          }
        });
      } else {
        response.json().then((data: { message: string }) => {
          const errorObj = JSON.parse(data.message);
          if (errorObj.errorCode === 'FAILED_TO_PARSE_PROGRAM_INVITATIONS_CSV') {
            this.setState({ error: { message: errorObj.description, type: 'error' }, loading: false });
          } else {
            this.setState({ error: getDefaultError(), loading: false });
          }
        });
      }
    });
  };

  onFileDrop = ({ files }: { files: File[] }) => {
    this.setState({ isInitialState: false });
    const file = files && files[0];
    if (file) {
      const { setFile } = this.props;
      setFile(file);
      this.setState({ error: undefined });
    }
  };

  onFileDelete = () => {
    const { setFile } = this.props;
    setFile(undefined);
    this.setState({ error: undefined });
  };

  getInvitationError = (totalSkippedCount: number): Error => {
    let message: string | JSX.Element;
    if (totalSkippedCount > 0) {
      message = (
        <FormattedMessage
          message={_t(
            `There are no new invitations that can be sent with {skippedCount}
            {skippedCount, plural, =1 {row} other {rows}}
            skipped. Please fix the issues in the file and re-upload.`
          )}
          skippedCount={totalSkippedCount}
        />
      );
    } else {
      message = _t(
        'File is either empty or there are no new invitations. Please fix any issues in the file and re-upload.'
      );
    }
    return { message, type: 'warning' };
  };

  getDefaultFileMessage = () => {
    const { file } = this.props;
    if (file) {
      return _t('Will replace current file');
    } else {
      return _t('You can upload 1 file at a time');
    }
  };

  disableUpload = () => {
    const { loading, error } = this.state;
    const { file, didAgreeToTerms, onAgreementToTermsChange } = this.props;
    if (onAgreementToTermsChange) {
      return !file || loading || !!error || !didAgreeToTerms;
    } else {
      return !file || loading || !!error;
    }
  };

  render() {
    const { loading, error, uploadAttemptWithoutFile, isInitialState } = this.state;
    const { handleClose, onPreviousStep, file, didAgreeToTerms, onAgreementToTermsChange, courseId } = this.props;
    return (
      <div className="ImportLearnersCSVUpload">
        <Typography2 component="h2" variant="titleSmall" className="m-b-2">
          {_t('Choose file')}
        </Typography2>
        <Typography2
          component="p"
          css={css`
            margin-bottom: var(--cds-spacing-200);
          `}
        >
          <FormattedMessage
            message={_t('To add and invite learners, {csvTemplateLink}')}
            csvTemplateLink={
              <a href={CSV_TEMPLATE_URL} rel="noopener noreferrer" target="_blank">
                {_t('download the .csv template')}
              </a>
            }
          />
        </Typography2>
        {!loading && (
          <FileUploadWidget
            defaultFileMessage={this.getDefaultFileMessage()}
            onDrop={this.onFileDrop}
            accept={{ 'text/csv': ['.csv'] }}
            singleFileOnly={true}
          >
            {file && <CSVPreUploadPreview files={[file]} onDelete={this.onFileDelete} />}
          </FileUploadWidget>
        )}
        {loading && (
          <div className="vertical-box align-items-absolute-center p-a-3">
            <LoadingSection label={_t('Uploading...')} />
          </div>
        )}
        {error && (
          <InlineNotification severity={error.type} style={{ marginTop: 10 }}>
            {error.message}
          </InlineNotification>
        )}
        {onAgreementToTermsChange && (
          <div>
            <div
              css={css`
                margin-top: var(--cds-spacing-200);
              `}
            >
              <Checkbox
                onChange={onAgreementToTermsChange}
                checked={didAgreeToTerms}
                label={
                  (!courseId ? (
                    <FormattedMessage
                      message={_t(
                        `I agree to use this tool for sending messages related to Coursera learning programs. Any breach of this is against Coursera's {terms} and {privacy} and may result in account suspension.`
                      )}
                      terms={<a href="/about/terms">{_t('Terms of Use')}</a>}
                      privacy={<a href="/about/privacy">{_t('Privacy Policy')}</a>}
                    />
                  ) : (
                    <FormattedMessage
                      message={_t(
                        `I understand that each invitation sent counts against my available licenses. Once they join, {learners} in the course I've selected for them. I agree to use this tool for sending messages related to Coursera learning programs. Any breach of this is against Coursera's {terms} and {privacy} and may result in account suspension.`
                      )}
                      learners={<strong>{_t('invited learners will be auto-enrolled')}</strong>}
                      terms={<a href="/about/terms">{_t('Terms of Use')}</a>}
                      privacy={<a href="/about/privacy">{_t('Privacy Policy')}</a>}
                    />
                  )) as unknown as string
                }
              />
            </div>
            {!didAgreeToTerms && !isInitialState && <p css={confirm}>{_t('Please confirm to continue')}</p>}
            {uploadAttemptWithoutFile && <p css={confirm}>{_t('Please upload a file to continue')}</p>}
          </div>
        )}
        <ModalFooter
          onContinue={this.handleS3Upload}
          onContinueLabel={_t('Upload')}
          disabled={this.disableUpload()}
          onCancel={handleClose}
          onPrevious={onAgreementToTermsChange ? undefined : onPreviousStep}
        />
      </div>
    );
  }
}

export default ImportLearnersCSVUpload;
